/**
 * Outil « Arbre et carré de Bayes » (Boîte à outils, 4e → prépa).
 *
 * Deux épreuves (A puis B) réglées par trois curseurs en % : l'arbre
 * pondéré, le carré découpé (aires = probabilités des chemins) et le tableau
 * croisé sur 10 000 personnes bougent ensemble. Résultats exacts : P(A ∩ B),
 * P(B) par la formule des probabilités totales et, dès la 2nde, P_B(A) —
 * l'inversion de la condition (le scénario du dépistage fait d'abord deviner).
 * Simulation : des points tirés au hasard dans le carré. Calculs : `bayes.ts`.
 */

import { useMemo, useState } from 'react';
import { schoolLevelRank } from '@jeraisonne/db/cards';
import { MathText, cn } from '@jeraisonne/ui';
import { SendToSheetButton } from '../../send-to-sheet-button';
import type { ToolProps } from '../../tool-types';
import type { ExactNumber } from '../../ui/exact';
import { Note, ParamSlider, SECONDARY_BUTTON, Segmented } from '../../ui/controls';
import { formatNumber } from '../../ui/format';
import { FIGURE_BG_CLASS, HaloText, INK } from '../../ui/svg-parts';
import {
  joint,
  percent,
  POPULATION,
  results,
  SCENARIOS,
  SCREENING_GUESSES,
  closestGuess,
  simCounts,
  simulate,
  type BayesParams,
  type ScenarioId,
  type SimPoint,
} from './bayes';

type View = 'tree' | 'square' | 'table';

const AB_COLOR = 'var(--color-primary-500)';
const NOTA_B_COLOR = 'var(--color-accent-500)';
const NOT_B = 'var(--color-ink-100)';
/** au-delà, on compte sans dessiner (10 000 cercles alourdiraient la page) */
const DRAWN_POINTS = 1000;

/** décimal exact d'un pourcentage ou d'un dix-millième : 0,99 · 0,0099 */
const dec = (v: number) => formatNumber(v, 4);
const tx = (s: string) => s.replace(/,/g, '{,}').replace(/−/g, '-');
const bar = (letter: string) => `${letter}̄`;
const count = (n: number) => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

/**
 * Ce que le niveau permet : collège = chemins et sommes de chemins ; 2nde =
 * ∩, Ā et proportions lues sur les effectifs (pas de P_A(B), notion de 1re) ;
 * lycée (1re, Tle) = P_A(B), probabilités totales, inversion ; prépa = P(B | A).
 */
type Mode = 'college' | 'seconde' | 'lycee' | 'prepa';

function modeOf(rank: number | null): Mode {
  if (rank === null) return 'lycee';
  if (rank <= 4) return 'college';
  if (rank === 5) return 'seconde';
  return rank >= 8 ? 'prepa' : 'lycee';
}

export function BayesTreeTool({
  onSend,
  sendCost,
  level,
  rng = Math.random,
}: ToolProps & { level?: string | null; rng?: () => number }) {
  const mode = modeOf(schoolLevelRank(level));
  const college = mode === 'college';
  const conditional = mode === 'lycee' || mode === 'prepa';
  const [scenarioId, setScenarioId] = useState<ScenarioId>(college ? 'free' : 'screening');
  const scenario = SCENARIOS.find((s) => s.id === scenarioId)!;
  const names = conditional ? scenario.names : (scenario.simpleNames ?? scenario.names);
  const [p, setP] = useState<BayesParams>(scenario.params);
  const [view, setView] = useState<View>('tree');
  const [guess, setGuess] = useState<{ choice: number; right: boolean } | null>(null);
  const [points, setPoints] = useState<SimPoint[]>([]);
  const r = useMemo(() => results(p), [p]);
  const j = joint(p);
  const A = scenario.A.letter;
  const B = scenario.B.letter;

  const choose = (id: ScenarioId) => {
    const s = SCENARIOS.find((x) => x.id === id)!;
    setScenarioId(id);
    setP(s.params);
    setGuess(null);
    setPoints([]);
  };
  const set = (key: keyof BayesParams) => (v: number) => {
    setP((old) => ({ ...old, [key]: v }));
    setPoints([]);
  };
  const answer = (choice: number) =>
    setGuess({ choice, right: closestGuess((r.pAgivenB?.value ?? 0) * 100) === choice });

  /** P_A(B) au lycée, P(B | A) en prépa (KaTeX) */
  const cond = (given: string, ev: string) =>
    mode === 'prepa' ? `P(${ev} \\mid ${given})` : `P_{${given}}(${ev})`;
  const notTex = (l: string) => `\\overline{${l}}`;
  const askFirst = scenarioId === 'screening' && guess === null;
  const pct = (v: number) => tx(percent(v)).replace('%', '\\,\\%');

  return (
    <div className="space-y-4">
      <Segmented
        ariaLabel="Situation"
        value={scenarioId}
        options={SCENARIOS.filter((s) => !college || s.id !== 'screening').map((s) => ({
          value: s.id,
          label: s.label,
        }))}
        onChange={choose}
      />
      <p className="text-ink-700 text-sm">
        <strong>{A}</strong> : {scenario.A.text} ; <strong>{B}</strong> : {scenario.B.text}.
      </p>

      <div className="space-y-2">
        <ParamSlider
          name={names.a}
          ariaLabel={names.a}
          value={p.a}
          min={1}
          max={99}
          step={1}
          format={(v) => `${v} %`}
          onChange={set('a')}
          color={AB_COLOR}
        />
        <ParamSlider
          name={names.b1}
          ariaLabel={names.b1}
          value={p.b1}
          min={0}
          max={100}
          step={1}
          format={(v) => `${v} %`}
          onChange={set('b1')}
        />
        <ParamSlider
          name={names.b2}
          ariaLabel={names.b2}
          value={p.b2}
          min={0}
          max={100}
          step={1}
          format={(v) => `${v} %`}
          onChange={set('b2')}
        />
      </div>

      <Segmented
        ariaLabel="Représentation"
        value={view}
        options={[
          { value: 'tree', label: 'Arbre' },
          { value: 'square', label: 'Carré' },
          { value: 'table', label: 'Tableau' },
        ]}
        onChange={setView}
      />

      {view === 'tree' && <TreeFigure p={p} A={A} B={B} />}
      {view === 'square' && (
        <>
          <SquareFigure p={p} A={A} B={B} points={points} />
          <SimulationPanel
            p={p}
            A={A}
            B={B}
            points={points}
            onSimulate={(n) => setPoints(simulate(p, n, rng))}
            showRatio={!college}
          />
        </>
      )}
      {view === 'table' && <CrossTable p={p} A={A} B={B} highlight={!college && !askFirst} />}

      {askFirst ? (
        <section
          className="border-ink-100 space-y-2 rounded-2xl border p-3"
          data-testid="bayes-guess"
        >
          <p className="text-ink-900 text-sm font-semibold">
            Devine d’abord : le test est positif. Quelle est la probabilité que la personne soit
            malade ?
          </p>
          <div className="grid grid-cols-2 gap-2">
            {SCREENING_GUESSES.map((g) => (
              <button
                key={g.value}
                type="button"
                className={SECONDARY_BUTTON}
                onClick={() => answer(g.value)}
              >
                {g.label}
              </button>
            ))}
          </div>
        </section>
      ) : (
        <section
          className="border-ink-100 space-y-3 rounded-2xl border px-3 py-3"
          aria-label="Résultats"
          data-testid="bayes-results"
        >
          {scenarioId === 'screening' && guess !== null && r.pAgivenB && (
            <Note tone={guess.right ? 'success' : 'error'} live={false}>
              {guess.right ? 'Bien vu. ' : 'Surprenant, non ? '}
              Sur {count(POPULATION)} personnes : {count(j.AB)} malades testés positifs (vrais
              positifs) et {count(j.notAB)} non-malades testés positifs (faux positifs). Parmi les{' '}
              {count(j.AB + j.notAB)} positifs, {count(j.AB)} sont malades :{' '}
              {percent(r.pAgivenB.value)}.
              {j.notAB > j.AB &&
                ' Les non-malades sont si nombreux que leurs faux positifs dépassent les vrais positifs.'}
            </Note>
          )}
          <ResultLine
            tex={
              college
                ? `\\text{chemin } ${A} \\text{ puis } ${B} : ${tx(dec(p.a / 100))} \\times ${tx(dec(p.b1 / 100))} = ${r.pAB.tex}`
                : conditional
                  ? `P(${A} \\cap ${B}) = P(${A}) \\times ${cond(A, B)} = ${tx(dec(p.a / 100))} \\times ${tx(dec(p.b1 / 100))} = ${r.pAB.tex}`
                  : `P(${A} \\cap ${B}) = \\frac{${j.AB}}{${tx(count(POPULATION)).replace(/\u202f/g, '\\,')}} = ${r.pAB.tex}`
            }
            exact={r.pAB}
            onSend={onSend}
            sendCost={sendCost}
            caption={
              mode === 'seconde'
                ? `Lu dans le tableau sur ${count(POPULATION)} individus.`
                : undefined
            }
          />
          <ResultLine
            tex={
              college
                ? `P(${B}) = ${r.pAB.tex} + ${tx(dec(j.notAB / POPULATION))} = ${r.pB.tex}`
                : `P(${B}) = P(${A} \\cap ${B}) + P(${notTex(A)} \\cap ${B}) = ${r.pAB.tex} + ${tx(dec(j.notAB / POPULATION))} = ${r.pB.tex}`
            }
            exact={r.pB}
            onSend={onSend}
            sendCost={sendCost}
            caption={
              college
                ? `On ajoute les chemins qui mènent à ${B}.`
                : conditional
                  ? 'Formule des probabilités totales.'
                  : `Les deux cas ne peuvent pas arriver ensemble : on ajoute.`
            }
          />
          {mode === 'seconde' && (
            <ResultLine
              tex={
                j.AB + j.notAB > 0
                  ? `\\frac{${j.AB}}{${j.AB + j.notAB}} = ${fractionTex(j.AB, j.AB + j.notAB)}${r.pAgivenB && r.pAgivenB.plain.includes('/') ? ` \\approx ${pct(r.pAgivenB.value)}` : ''}`
                  : `\\text{aucun cas où } ${B} \\text{ est réalisé}`
              }
              exact={r.pAgivenB}
              onSend={onSend}
              sendCost={sendCost}
              caption={`Parmi les ${count(j.AB + j.notAB)} cas où ${B} est réalisé, ${count(j.AB)} ont aussi ${A} : c’est la proportion de ${A} parmi ${B}.`}
              testId="bayes-inverse"
            />
          )}
          {conditional && (
            <ResultLine
              tex={
                r.pAgivenB
                  ? `${cond(B, A)} = \\frac{P(${A} \\cap ${B})}{P(${B})} = ${r.pAgivenB.tex}${r.pAgivenB.plain.includes('/') ? ` \\approx ${pct(r.pAgivenB.value)}` : ''}`
                  : `P(${B}) = 0 : ${cond(B, A)} \\text{ n’est pas définie}`
              }
              exact={r.pAgivenB}
              onSend={onSend}
              sendCost={sendCost}
              caption={`On inverse la condition : parmi les cas où ${B} est réalisé, quelle part a aussi ${A} ?`}
              testId="bayes-inverse"
            />
          )}
          {r.independent && conditional && (
            <Note live={false}>
              <MathText>{`$${cond(A, B)} = ${cond(notTex(A), B)}$`}</MathText> : savoir si {A} est
              réalisé ne change rien pour {B}. Les événements {A} et {B} sont indépendants.
            </Note>
          )}
        </section>
      )}
    </div>
  );
}

/** a/b réduite en KaTeX (\frac{1}{6}), ou entier */
function fractionTex(a: number, b: number): string {
  let x = a;
  let y = b;
  while (y) [x, y] = [y, x % y];
  const g = x || 1;
  return b / g === 1 ? String(a / g) : `\\frac{${a / g}}{${b / g}}`;
}

function ResultLine({
  tex,
  exact,
  onSend,
  sendCost,
  caption,
  testId,
}: {
  tex: string;
  exact: ExactNumber | null;
  onSend?: ToolProps['onSend'];
  sendCost: number;
  caption?: string;
  testId?: string;
}) {
  return (
    <div className="space-y-1" data-testid={testId}>
      <MathText className="text-ink-900 block overflow-x-auto text-base">{`$${tex}$`}</MathText>
      <div className="flex flex-wrap items-center gap-2">
        {caption && <p className="text-ink-600 min-w-0 flex-1 text-sm">{caption}</p>}
        {onSend && exact && (
          <SendToSheetButton
            label={exact.plain}
            cost={sendCost}
            onClick={() => onSend({ atoms: exact.atoms })}
          />
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Figures

const W = 320;

function TreeFigure({ p, A, B }: { p: BayesParams; A: string; B: string }) {
  const H = 270;
  const j = joint(p);
  const root = { x: 18, y: H / 2 };
  const n1 = [
    { x: 118, y: 70, label: A, prob: p.a / 100 },
    { x: 118, y: 200, label: bar(A), prob: 1 - p.a / 100 },
  ];
  const leaves = [
    { from: 0, x: 222, y: 30, label: B, prob: p.b1 / 100, path: j.AB, isB: true },
    { from: 0, x: 222, y: 110, label: bar(B), prob: 1 - p.b1 / 100, path: j.AnotB, isB: false },
    { from: 1, x: 222, y: 160, label: B, prob: p.b2 / 100, path: j.notAB, isB: true },
    { from: 1, x: 222, y: 240, label: bar(B), prob: 1 - p.b2 / 100, path: j.notAnotB, isB: false },
  ];
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className={cn('border-ink-100 block w-full rounded-2xl border', FIGURE_BG_CLASS)}
      role="img"
      aria-label={`Arbre pondéré : P(${A}) = ${dec(p.a / 100)}, puis ${B} avec ${dec(p.b1 / 100)} après ${A} et ${dec(p.b2 / 100)} après non ${A}.`}
      data-testid="bayes-tree"
    >
      {n1.map((n, i) => (
        <g key={i}>
          <line x1={root.x} y1={root.y} x2={n.x - 12} y2={n.y} stroke={INK} strokeWidth={1.5} />
          <HaloText
            x={(root.x + n.x) / 2 - 2}
            y={(root.y + n.y) / 2 + (i === 0 ? -6 : 16)}
            textAnchor="middle"
            fontSize={13}
            fill="var(--color-ink-700)"
          >
            {dec(n.prob)}
          </HaloText>
          <text x={n.x} y={n.y + 5} textAnchor="middle" fontSize={16} fontWeight={700} fill={INK}>
            {n.label}
          </text>
        </g>
      ))}
      {leaves.map((l, i) => {
        const from = n1[l.from]!;
        const color = l.isB ? (l.from === 0 ? AB_COLOR : NOTA_B_COLOR) : INK;
        // texte doré : l'or 500 est trop pâle sur fond blanc
        const textColor = l.isB && l.from === 1 ? 'var(--color-accent-700)' : color;
        return (
          <g key={i}>
            <line
              x1={from.x + 12}
              y1={from.y}
              x2={l.x - 12}
              y2={l.y}
              stroke={color}
              strokeWidth={l.isB ? 2.5 : 1.5}
            />
            <HaloText
              x={(from.x + l.x) / 2}
              y={(from.y + l.y) / 2 + (l.y < from.y ? -6 : 16)}
              textAnchor="middle"
              fontSize={13}
              fill="var(--color-ink-700)"
            >
              {dec(l.prob)}
            </HaloText>
            <text
              x={l.x}
              y={l.y + 5}
              textAnchor="middle"
              fontSize={16}
              fontWeight={700}
              fill={textColor}
            >
              {l.label}
            </text>
            <text
              x={W - 6}
              y={l.y + 5}
              textAnchor="end"
              fontSize={13}
              fontWeight={l.isB ? 700 : 400}
              fill={l.isB ? textColor : 'var(--color-ink-500)'}
            >
              {dec(l.path / POPULATION)}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function SquareFigure({
  p,
  A,
  B,
  points,
}: {
  p: BayesParams;
  A: string;
  B: string;
  points: readonly SimPoint[];
}) {
  const x0 = 34;
  const y0 = 8;
  const s = 276;
  const H = y0 + s + 30;
  const wa = (p.a / 100) * s;
  const h1 = (p.b1 / 100) * s;
  const h2 = (p.b2 / 100) * s;
  const j = joint(p);
  const label = (x: number, y: number, w: number, h: number, v: number, on: string) =>
    w > 46 && h > 18 ? (
      <text
        x={x + w / 2}
        y={y + h / 2 + 5}
        textAnchor="middle"
        fontSize={12}
        fontWeight={700}
        fill={on}
      >
        {dec(v / POPULATION)}
      </text>
    ) : null;
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className={cn('border-ink-100 block w-full rounded-2xl border', FIGURE_BG_CLASS)}
      role="img"
      aria-label={`Carré unité découpé : colonne ${A} de largeur ${dec(p.a / 100)}, et dans chaque colonne la partie ${B} en haut ; les aires sont les probabilités des chemins.`}
      data-testid="bayes-square"
    >
      <rect x={x0} y={y0 + h1} width={wa} height={s - h1} fill={NOT_B} />
      <rect x={x0 + wa} y={y0 + h2} width={s - wa} height={s - h2} fill={NOT_B} />
      <rect x={x0} y={y0} width={wa} height={h1} fill={AB_COLOR} fillOpacity={0.8} />
      <rect x={x0 + wa} y={y0} width={s - wa} height={h2} fill={NOTA_B_COLOR} fillOpacity={0.7} />
      <rect x={x0} y={y0} width={s} height={s} fill="none" stroke={INK} strokeWidth={1.5} />
      <line x1={x0 + wa} y1={y0} x2={x0 + wa} y2={y0 + s} stroke={INK} strokeWidth={1.5} />
      {label(x0, y0, wa, h1, j.AB, '#fff')}
      {label(x0 + wa, y0, s - wa, h2, j.notAB, 'var(--color-on-accent)')}
      {label(x0, y0 + h1, wa, s - h1, j.AnotB, 'var(--color-ink-700)')}
      {label(x0 + wa, y0 + h2, s - wa, s - h2, j.notAnotB, 'var(--color-ink-700)')}
      {points.slice(0, DRAWN_POINTS).map((pt, i) => (
        <circle
          key={i}
          cx={x0 + pt.x * s}
          cy={y0 + pt.y * s}
          r={1.8}
          fill={pt.B ? 'var(--color-ink-900)' : 'var(--color-ink-400)'}
        />
      ))}
      {/* la colonne A reste nommée même étroite (dépistage : 1 %) */}
      <text
        x={wa > 14 ? x0 + wa / 2 : x0 + 2}
        y={H - 8}
        textAnchor={wa > 14 ? 'middle' : 'start'}
        fontSize={14}
        fontWeight={700}
        fill={INK}
      >
        {A}
      </text>
      <text
        x={x0 + wa + (s - wa) / 2}
        y={H - 8}
        textAnchor="middle"
        fontSize={14}
        fontWeight={700}
        fill={INK}
      >
        {s - wa > 28 ? bar(A) : ''}
      </text>
      <text x={14} y={y0 + 16} textAnchor="middle" fontSize={14} fontWeight={700} fill={AB_COLOR}>
        {B}
      </text>
    </svg>
  );
}

function SimulationPanel({
  p,
  A,
  B,
  points,
  onSimulate,
  showRatio,
}: {
  p: BayesParams;
  A: string;
  B: string;
  points: readonly SimPoint[];
  onSimulate: (n: number) => void;
  showRatio: boolean;
}) {
  const c = simCounts(points);
  const theory = results(p).pAgivenB;
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {[100, 1000, 10000].map((n) => (
          <button
            key={n}
            type="button"
            aria-label={`Simuler ${n} individus`}
            className={SECONDARY_BUTTON}
            onClick={() => onSimulate(n)}
          >
            Simuler {count(n)} individus
          </button>
        ))}
      </div>
      {points.length > 0 && (
        <p className="text-ink-800 text-sm" data-testid="bayes-sim">
          Un point = un individu tiré au hasard
          {points.length > DRAWN_POINTS && ` (${count(DRAWN_POINTS)} dessinés)`}. Sur{' '}
          {count(points.length)}, {count(c.B)} ont {B}
          {showRatio && c.B > 0 && (
            <>
              , dont {count(c.AB)} ont aussi {A} : fréquence {c.AB}/{c.B} ≈{' '}
              {formatNumber(c.AB / c.B, 3)}
              {theory && <> (probabilité : {formatNumber(theory.value, 3)})</>}
            </>
          )}
          .
        </p>
      )}
    </div>
  );
}

function CrossTable({
  p,
  A,
  B,
  highlight,
}: {
  p: BayesParams;
  A: string;
  B: string;
  highlight: boolean;
}) {
  const j = joint(p);
  const rows = [
    { label: A, b: j.AB, nb: j.AnotB },
    { label: bar(A), b: j.notAB, nb: j.notAnotB },
  ];
  const cell = 'py-2 text-right tabular-nums';
  return (
    <div className="space-y-1">
      <table className="w-full text-sm" data-testid="bayes-table">
        <caption className="text-ink-600 pb-1 text-left text-xs">
          Effectifs sur {count(POPULATION)} individus
        </caption>
        <thead>
          <tr className="text-ink-500 text-xs">
            <th />
            <th className="py-1 text-right font-semibold">{B}</th>
            <th className="py-1 text-right font-semibold" aria-label={`non ${B}`}>
              {bar(B)}
            </th>
            <th className="py-1 text-right font-semibold">Total</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-ink-100 border-t">
              <th
                className="text-ink-900 py-2 text-left font-semibold"
                aria-label={i === 0 ? r.label : `non ${A}`}
              >
                {r.label}
              </th>
              <td className={cn(cell, highlight && i === 0 && 'bg-primary-50 font-bold')}>
                {count(r.b)}
              </td>
              <td className={cell}>{count(r.nb)}</td>
              <td className={cell}>{count(r.b + r.nb)}</td>
            </tr>
          ))}
          <tr className="border-ink-200 border-t">
            <th className="text-ink-900 py-2 text-left font-semibold">Total</th>
            <td className={cn(cell, highlight && 'bg-primary-50 font-bold')}>
              {count(j.AB + j.notAB)}
            </td>
            <td className={cell}>{count(j.AnotB + j.notAnotB)}</td>
            <td className={cell}>{count(POPULATION)}</td>
          </tr>
        </tbody>
      </table>
      {highlight && j.AB + j.notAB > 0 && (
        <p className="text-ink-600 text-sm">
          Parmi les {count(j.AB + j.notAB)} cas où {B} est réalisé, {count(j.AB)} ont aussi {A}.
        </p>
      )}
    </div>
  );
}
